// app/javascript/controllers/funnel_checkout_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "totalPrice",
    "bumpTotal",
    "bumpSummary",
    "bumpCheckbox",
    "moduleToggle",
    "submitButton",
    "buttonPrice",        // NEW: Dynamic button price
    "savingsRow",         // NEW: Savings CRO row
    "savingsAmount",      // NEW: Savings amount
    "paymentSection",     // NEW: payment-method block (hidden when free)
    "discountAmount"      // NEW: promo discount line in the summary
  ]

  static values = {
    basePrice: Number,
    currency: { type: String, default: "TND" },
    discountKind: { type: String, default: "" },   // "percentage" | "fixed_amount"
    discountValue: { type: Number, default: 0 }    // % for percentage, TND for fixed
  }

  static classes = ["selected"]

  connect() {
    console.log("💳 Funnel checkout controller connected")
    console.log("Base price:", this.basePriceValue, this.currencyValue)

    // Recalculate on EVERY connect (Turbo-safe)
    this.updateTotal()

    // Re-sync visual state with checkbox state
    this.syncBumpVisuals()
  }

  turboBeforeCache() {
    console.log("💳 Preparing for Turbo cache")
  }

  toggleModules(event) {
    const button = event.currentTarget
    const productId = button.dataset.productId
    const modulesDiv = document.getElementById(`modules-${productId}`)
    const arrow = document.getElementById(`arrow-${productId}`)

    if (!modulesDiv || !arrow) {
      console.warn(`Modules div or arrow not found for product ${productId}`)
      return
    }

    const isOpen = modulesDiv.classList.contains('open')

    if (isOpen) {
      modulesDiv.classList.remove('open')
      arrow.style.transform = 'rotate(0deg)'
      button.setAttribute('aria-expanded', 'false')
    } else {
      modulesDiv.classList.add('open')
      arrow.style.transform = 'rotate(180deg)'
      button.setAttribute('aria-expanded', 'true')
    }
  }

  toggleBump(event) {
    const checkbox = event.currentTarget
    const bumpId = checkbox.value
    const bumpPrice = this.getPriceFromCheckbox(checkbox)

    console.log(`Bump ${bumpId}: ${checkbox.checked ? 'ADDED' : 'REMOVED'} (${bumpPrice} ${this.currencyValue})`)

    // Update visual state
    this.updateBumpCardVisual(checkbox)

    // Recalculate total
    this.updateTotal()
  }

  syncBumpVisuals() {
    this.bumpCheckboxTargets.forEach(checkbox => {
      this.updateBumpCardVisual(checkbox)
    })
  }

  updateBumpCardVisual(checkbox) {
    const card = checkbox.closest('.order-bump-card')
    if (!card) return

    if (checkbox.checked) {
      card.classList.add('selected')
      card.setAttribute('aria-checked', 'true')
    } else {
      card.classList.remove('selected')
      card.setAttribute('aria-checked', 'false')
    }
  }

  updateTotal() {
    let total = this.basePriceValue
    let bumpsTotal = 0
    let originalBumpsValue = 0  // Full price before "65% discount"

    // Find all checked bumps
    const checkedBumps = this.bumpCheckboxTargets.filter(cb => cb.checked)

    checkedBumps.forEach(checkbox => {
      const price = this.getPriceFromCheckbox(checkbox)
      bumpsTotal += price
      // Calculate original value (bump price shown as 65% off)
      originalBumpsValue += price * 2.85  // Same ratio as displayed
    })

    const subtotal = total + bumpsTotal

    // Apply the promo code to the WHOLE cart (products + bumps).
    const promo = this.#discountFor(subtotal)
    total = Math.max(subtotal - promo, 0)
    const isFree = total <= 0

    // Calculate savings
    const savings = originalBumpsValue - bumpsTotal

    // Promo discount line in the summary (recomputes as bumps toggle).
    if (this.hasDiscountAmountTarget) {
      this.discountAmountTarget.textContent = "-" + this.formatPrice(promo)
    }

    // Update main total
    if (this.hasTotalPriceTarget) {
      this.totalPriceTarget.textContent = this.formatPrice(total)
    }

    // Update button price (GRATUIT when the order is free)
    if (this.hasButtonPriceTarget) {
      this.buttonPriceTarget.textContent = isFree ? "GRATUIT" : this.formatPrice(total)
    }

    // Nothing to pay → hide the payment-method picker.
    if (this.hasPaymentSectionTarget) {
      this.paymentSectionTarget.classList.toggle("hidden", isFree)
    }

    // Update bump breakdown
    if (this.hasBumpTotalTarget && this.hasBumpSummaryTarget) {
      if (bumpsTotal > 0) {
        this.bumpTotalTarget.textContent = bumpsTotal.toFixed(2)
        this.bumpSummaryTarget.classList.remove('hidden')
      } else {
        this.bumpSummaryTarget.classList.add('hidden')
      }
    }

    // Update savings display (CRO element)
    if (this.hasSavingsRowTarget && this.hasSavingsAmountTarget) {
      if (savings > 0) {
        this.savingsAmountTarget.textContent = this.formatPrice(savings)
        this.savingsRowTarget.classList.remove('hidden')
      } else {
        this.savingsRowTarget.classList.add('hidden')
      }
    }

    console.log(`Total: ${this.basePriceValue} + ${bumpsTotal} = ${total} ${this.currencyValue} | Savings: ${savings.toFixed(2)}`)
  }

  // ✅ NEW: Safe price extraction with validation
  getPriceFromCheckbox(checkbox) {
    const priceStr = checkbox.dataset.price
    const price = parseFloat(priceStr)

    if (isNaN(price)) {
      console.error(`Invalid price for bump ${checkbox.value}: "${priceStr}"`)
      return 0
    }

    if (price < 0) {
      console.warn(`Negative price for bump ${checkbox.value}: ${price}`)
      return 0
    }

    return price
  }

  formatPrice(amount) {
    return `${amount.toFixed(2)} ${this.currencyValue}`
  }

  // Promo discount amount (in TND) for a given cart subtotal.
  #discountFor(subtotal) {
    const v = this.discountValueValue
    if (!v || v <= 0) return 0
    if (this.discountKindValue === "percentage") {
      return Math.min(subtotal * (v / 100), subtotal)
    }
    if (this.discountKindValue === "fixed_amount") {
      return Math.min(v, subtotal)
    }
    return 0
  }

  disconnect() {
    console.log("💳 Funnel checkout controller disconnected")
  }
}
